import _ from 'lodash'
import {
  background,
  border,
  color,
  flexbox,
  get,
  grid,
  position,
  shadow,
  space,
  system,
  typography,
  variant,
} from 'styled-system'
import { Border, Truncation } from './stylePropTypes'
import theme, { fontVariants } from './theme'

export {
  background,
  border,
  color,
  flexbox,
  grid,
  position,
  shadow,
  space,
  typography,
}

const fractionsToPercentages = (n: number, scale: unknown) =>
  get(scale, n, !_.isFinite(n) || n > 1 ? n : n * 100 + '%')

/**
 * This is a "fork" of the layout module of styled-system (at 031e11f).
 *
 * The only difference is that we allow fractional values to be treated the same for both width and
 * height. For whatever reason, upstream styled-system only does this for widths.
 *
 * @see https://github.com/styled-system/styled-system/blob/031e11f/packages/layout/src/index.js
 * @see https://styled-system.com/api#system
 */
export const layout = system({
  width: {
    property: 'width',
    scale: 'sizes',
    transform: fractionsToPercentages,
  },
  height: {
    property: 'height',
    scale: 'sizes',
    transform: fractionsToPercentages,
  },
  minWidth: {
    property: 'minWidth',
    scale: 'sizes',
  },
  minHeight: {
    property: 'minHeight',
    scale: 'sizes',
  },
  maxWidth: {
    property: 'maxWidth',
    scale: 'sizes',
  },
  maxHeight: {
    property: 'maxHeight',
    scale: 'sizes',
  },
  size: {
    properties: ['width', 'height'],
    scale: 'sizes',
  },
  overflow: true,
  overflowX: true,
  overflowY: true,
  display: true,
  verticalAlign: true,
})

export const fontVariant = variant({
  prop: 'font',
  variants: fontVariants,
})

export type FontVariants = keyof typeof fontVariants

/**
 * This meta-prop ensures that if you specify a borderColor for your component, a default border
 * style and width are also automatically set (which you can still override as needed).
 *
 * In the vast majority of cases where you set a border color, you also want that border to be,
 * you know, visible. So these two boxes will have identical rendering:
 *
 * @example <Box borderStyle="solid" borderWidth="thin" borderColor="black500" />
 * @example <Box borderColor="black500" />
 */
export const autoBorder = (p: Border) => {
  const styles: string[] = []
  if (!p.borderColor) return
  if (!p.borderStyle) styles.push('border-style: solid;')
  if (
    !p.borderWidth &&
    !p.borderTopWidth &&
    !p.borderLeftWidth &&
    !p.borderRightWidth &&
    !p.borderBottomWidth
  ) {
    styles.push(`border-width: ${theme.borderWidths.thin}px;`)
  }
  return styles.join('\n')
}

/**
 * Collection of miscellaneous style props that styled-system doesn't ship with, but are still
 * convenient to have accessible via the same mechanism.
 */
export const misc = system({
  whiteSpace: {
    property: 'whiteSpace',
  },
  transition: {
    property: 'transition',
  },
  transform: {
    property: 'transform',
  },
  cursor: {
    property: 'cursor',
  },
  pointerEvents: {
    property: 'pointerEvents',
  },
  textDecoration: {
    property: 'textDecoration',
  },
  textDecorationColor: {
    property: 'textDecorationColor',
  },
  textUnderlineOffset: {
    property: 'textUnderlineOffset',
  },
  textTransform: {
    property: 'textTransform',
  },
  gap: {
    property: 'gap',
  },
  isolation: {
    property: 'isolation',
  },
})

/**
 * Style props for truncating text with ellipsis, either for a single line or several.
 *
 * Note that truncateText has subtly different properties from truncateLines={1}, hence keeping both
 * implementations around.
 *
 * Text-clamping isn't universally supported yet, but is by all the browsers we support.
 *
 * @see https://caniuse.com/?search=line-clamp
 * @see https://css-tricks.com/almanac/properties/l/line-clamp/
 */
export const truncation = (p: Truncation) => {
  if (p.truncateText) {
    return `
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `
  }
  if (p.truncateLines) {
    return `
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: ${p.truncateLines};
      -webkit-box-orient: vertical;
    `
  }
}
